import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import clsx from "clsx";
import {
  Activity,
  ChevronRight,
  Container,
  FolderKanban,
  Globe,
  LayoutDashboard,
  Network,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plug,
  Receipt,
  Server,
  Settings,
  UsersRound,
  Wallet,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import { DropdownMenu, Tooltip } from "radix-ui";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useProjects } from "../api/queries.js";
import { useIsAdmin } from "../api/users.js";
import {
  activeItem,
  sectionTarget,
  visibleNav,
  type ActiveItem,
  type NavIcon,
  type NavLink as NavLinkItem,
  type NavSection,
} from "./nav.js";
import { ProjectSwitcher } from "./project-switcher.js";

/*
 * The left block (docs/web.md, "Menu"): the project switcher on top, then
 * the menu of `nav.ts` — groups, sections, and the open section's
 * subsections. Three looks of the same tree: `full` (wide screens), `rail`
 * (collapsed to icons; a section's subsections open as a flyout menu,
 * names as tooltips) and `sheet` (the phone drawer: a section's click opens
 * it and keeps the drawer open, a subsection's click closes it).
 */

const ICONS: Record<NavIcon, LucideIcon> = {
  overview: LayoutDashboard,
  dashboard: Workflow,
  servers: Server,
  network: Network,
  apps: Package,
  kubernetes: Container,
  dns: Globe,
  monitoring: Activity,
  costs: Wallet,
  settings: Settings,
  projects: FolderKanban,
  orgCosts: Receipt,
  users: UsersRound,
  api: Plug,
};

const RAIL_KEY = "cs.nav.rail";

function storedRail(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === "1";
  } catch {
    // Storage refused (a private window): the menu starts expanded.
    return false;
  }
}

/** Whether the left block is collapsed to the icon rail; remembered per browser. */
export function useRail(): [boolean, (rail: boolean) => void] {
  const [rail, setRail] = useState(storedRail);
  const change = (next: boolean) => {
    setRail(next);
    try {
      localStorage.setItem(RAIL_KEY, next ? "1" : "0");
    } catch {
      // Not remembered; it still applies until the page reloads.
    }
  };
  return [rail, change];
}

export type NavMode = "full" | "rail" | "sheet";

/**
 * A menu route comes from data (`nav.ts`), not a literal the router's types
 * know; `nav.test.ts` checks every one against the router.
 */
function MenuAnchor({
  to,
  projectId,
  ...rest
}: {
  to: string;
  projectId: string | undefined;
  className?: string;
  onClick?: (() => void) | undefined;
  "aria-current"?: "page" | undefined;
  "aria-label"?: string;
  /** The full name when the row cuts it (a narrow left block). */
  title?: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to as "/projects/$projectId"}
      params={{ projectId: projectId ?? "" }}
      // The router marks an exact match itself (`aria-current`); on the pages below an item
      // ours does, and a fuzzy match would mark Overview on every page of the project.
      activeOptions={{ exact: true, includeSearch: false }}
      {...rest}
    />
  );
}

/** The names of rail items, shown on hover and focus. */
function Tip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          side="right"
          sideOffset={8}
          className="z-50 rounded-md bg-text px-2 py-1 text-xs text-surface shadow"
        >
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

const ROW = "flex min-h-9 items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm";
const IDLE = "text-text hover:bg-surface-2";
const CURRENT = "bg-accent-soft font-medium text-accent-strong";
const RAIL_BUTTON = "flex size-10 items-center justify-center rounded-lg";

/** A section or subsection not built yet: disabled, «скоро», a tooltip saying so. */
function SoonItem({
  label,
  icon: Icon,
  rail,
  sub,
}: {
  label: string;
  icon?: LucideIcon | undefined;
  rail: boolean;
  sub?: boolean;
}) {
  const { t } = useTranslation();
  const hint = t("nav.soonHint", { name: label });
  return (
    <Tip label={hint}>
      <button
        type="button"
        aria-disabled="true"
        onClick={(e) => e.preventDefault()}
        className={clsx(
          "cursor-default text-muted/80",
          rail ? RAIL_BUTTON : clsx(ROW, "w-full text-left", sub && "pl-10"),
        )}
      >
        {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
        <span className={rail ? "sr-only" : "min-w-0 flex-1 truncate"}>{label}</span>
        <span
          className={clsx(
            rail
              ? "sr-only"
              : "rounded bg-surface-2 px-1.5 py-0.5 text-[0.65rem] font-medium uppercase tracking-wide",
          )}
        >
          {t("nav.soon")}
        </span>
      </button>
    </Tip>
  );
}

function Subsections({
  section,
  active,
  projectId,
  onNavigate,
}: {
  section: NavSection;
  active: ActiveItem | null;
  projectId: string | undefined;
  onNavigate: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  return (
    <ul className="flex flex-col gap-0.5 pt-0.5" aria-label={t(section.label)}>
      {(section.children ?? []).map((child) => (
        <li key={child.id}>
          {child.soon ? (
            <SoonItem label={t(child.label)} rail={false} sub />
          ) : (
            <MenuAnchor
              to={child.to}
              projectId={projectId}
              onClick={onNavigate}
              aria-current={active?.link === child.id ? "page" : undefined}
              title={t(child.label)}
              className={clsx(ROW, "pl-10", active?.link === child.id ? CURRENT : IDLE)}
            >
              <span className="min-w-0 flex-1 truncate">{t(child.label)}</span>
            </MenuAnchor>
          )}
        </li>
      ))}
    </ul>
  );
}

function FullSection({
  section,
  active,
  projectId,
  sheet,
  onNavigate,
}: {
  section: NavSection;
  active: ActiveItem | null;
  projectId: string | undefined;
  sheet: boolean;
  onNavigate: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const Icon = ICONS[section.icon];
  const label = t(section.label);
  if (section.soon) return <SoonItem label={label} icon={Icon} rail={false} />;
  const target = sectionTarget(section) ?? "/";
  const open = active?.section === section.id;
  if (!section.children)
    return (
      <MenuAnchor
        to={target}
        projectId={projectId}
        onClick={onNavigate}
        aria-current={open ? "page" : undefined}
        title={label}
        className={clsx(ROW, open ? CURRENT : IDLE)}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{label}</span>
      </MenuAnchor>
    );
  return (
    <>
      {/* A disclosure that also goes to the first subsection (only the active section is
          open). A button, not a link: the router would mark it current with that subsection. */}
      <button
        type="button"
        aria-expanded={open}
        title={label}
        onClick={() => {
          void navigate({
            to: target as "/projects/$projectId",
            params: { projectId: projectId ?? "" },
          });
          // In the phone drawer the section opens and the drawer stays for its subsections.
          if (!sheet) onNavigate?.();
        }}
        className={clsx(ROW, IDLE, "w-full text-left", open && "font-medium")}
      >
        <Icon className="size-4 shrink-0" aria-hidden />
        <span className="min-w-0 flex-1 truncate">{label}</span>
        <ChevronRight
          className={clsx("size-4 shrink-0 text-muted transition-transform", open && "rotate-90")}
          aria-hidden
        />
      </button>
      {open && (
        <Subsections
          section={section}
          active={active}
          projectId={projectId}
          onNavigate={onNavigate}
        />
      )}
    </>
  );
}

function RailSection({
  section,
  active,
  projectId,
}: {
  section: NavSection;
  active: ActiveItem | null;
  projectId: string | undefined;
}) {
  const { t } = useTranslation();
  const Icon = ICONS[section.icon];
  const label = t(section.label);
  if (section.soon) return <SoonItem label={label} icon={Icon} rail />;
  const current = active?.section === section.id;
  const look = clsx(
    RAIL_BUTTON,
    current ? CURRENT : "text-muted hover:bg-surface-2 hover:text-text",
  );
  if (!section.children)
    return (
      <Tip label={label}>
        <MenuAnchor
          to={sectionTarget(section) ?? "/"}
          projectId={projectId}
          aria-label={label}
          aria-current={current ? "page" : undefined}
          className={look}
        >
          <Icon className="size-4" aria-hidden />
        </MenuAnchor>
      </Tip>
    );
  return (
    <DropdownMenu.Root>
      <Tip label={label}>
        <DropdownMenu.Trigger asChild>
          <button type="button" aria-label={label} className={look}>
            <Icon className="size-4" aria-hidden />
          </button>
        </DropdownMenu.Trigger>
      </Tip>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={8}
          className="z-50 min-w-56 rounded-xl border border-line bg-surface p-1 shadow-lg"
        >
          <DropdownMenu.Label className="px-3 pb-1 pt-2 text-[0.7rem] font-semibold uppercase tracking-wider text-muted">
            {label}
          </DropdownMenu.Label>
          {(section.children ?? []).map((child: NavLinkItem) =>
            child.soon ? (
              <DropdownMenu.Item
                key={child.id}
                disabled
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted"
              >
                {t(child.label)}
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Item key={child.id} asChild>
                <MenuAnchor
                  to={child.to}
                  projectId={projectId}
                  aria-current={active?.link === child.id ? "page" : undefined}
                  className={clsx(
                    "flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm outline-none data-[highlighted]:bg-surface-2",
                    active?.link === child.id && "font-medium text-accent-strong",
                  )}
                >
                  {t(child.label)}
                </MenuAnchor>
              </DropdownMenu.Item>
            ),
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * The menu of the left block. `projectId` is the project it shows
 * (`useNavProject`): its groups appear only with one.
 */
export function LeftNav({
  mode,
  projectId,
  onNavigate,
  onToggleRail,
}: {
  mode: NavMode;
  projectId: string | undefined;
  /** After a click on a destination (the phone drawer closes). */
  onNavigate?: () => void;
  /** Collapse or expand the left block (wide screens). */
  onToggleRail?: () => void;
}) {
  const { t } = useTranslation();
  const admin = useIsAdmin();
  const projects = useProjects();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const rail = mode === "rail";
  const list = projects.data;
  // Guests in every project they have never see the organisation's group (owner, 2026-10-22).
  const guestOnly =
    !admin && (!list || (list.length > 0 && list.every((p) => p.access === "guest")));
  const groups = visibleNav({ admin, guestOnly, inProject: projectId !== undefined });
  const active = activeItem(path);
  return (
    <Tooltip.Provider delayDuration={300}>
      <nav
        aria-label={t("nav.label")}
        className={clsx("flex min-h-0 flex-1 flex-col", rail ? "items-center" : "")}
      >
        <div className={clsx("shrink-0", rail ? "px-2 py-3" : "px-3 pb-2 pt-4")}>
          <ProjectSwitcher projectId={projectId} compact={rail} onPick={onNavigate} />
        </div>
        <div
          className={clsx(
            "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain pb-4",
            rail ? "items-center gap-2 px-2" : "gap-4 px-3 pt-2",
          )}
        >
          {groups.map((group, index) => {
            const header = group.label ? t(group.label) : undefined;
            return (
              <div key={group.id} className="flex flex-col gap-0.5">
                {header &&
                  (rail ? (
                    index > 0 && <span className="mx-auto my-1 h-px w-6 bg-line" aria-hidden />
                  ) : (
                    <p
                      id={`nav-group-${group.id}`}
                      className="px-3 pb-1 text-[0.7rem] font-semibold uppercase tracking-wider text-muted"
                    >
                      {header}
                    </p>
                  ))}
                <ul
                  className={clsx("flex flex-col", rail ? "items-center gap-1" : "gap-0.5")}
                  {...(header && !rail
                    ? { "aria-labelledby": `nav-group-${group.id}` }
                    : { "aria-label": header ?? t("nav.groups.project") })}
                >
                  {group.items.map((section) => (
                    <li key={section.id}>
                      {rail ? (
                        <RailSection section={section} active={active} projectId={projectId} />
                      ) : (
                        <FullSection
                          section={section}
                          active={active}
                          projectId={projectId}
                          sheet={mode === "sheet"}
                          onNavigate={onNavigate}
                        />
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        {onToggleRail && (
          <div className={clsx("shrink-0 border-t border-line", rail ? "px-2 py-2" : "px-3 py-2")}>
            {rail ? (
              <Tip label={t("nav.expand")}>
                <button
                  type="button"
                  onClick={onToggleRail}
                  aria-label={t("nav.expand")}
                  className={clsx(RAIL_BUTTON, "text-muted hover:bg-surface-2 hover:text-text")}
                >
                  <PanelLeftOpen className="size-4" aria-hidden />
                </button>
              </Tip>
            ) : (
              <button
                type="button"
                onClick={onToggleRail}
                className={clsx(ROW, "w-full text-muted hover:bg-surface-2 hover:text-text")}
              >
                <PanelLeftClose className="size-4 shrink-0" aria-hidden />
                {t("nav.collapse")}
              </button>
            )}
          </div>
        )}
      </nav>
    </Tooltip.Provider>
  );
}
